// src/api/quotationAPI.js - FIXED ENDPOINTS
import axiosInstance from './axiosConfig';

const quotationAPI = {
  // Get all quotations WITH ERROR HANDLING - FIXED ENDPOINT
  getQuotations: async () => {
    try {
      const res = await axiosInstance.get('/quotations'); // CHANGED: /quotation → /quotations
      return res.data;
    } catch (error) {
      console.error('❌ Quotations API error:', error.response?.data || error.message);
      return [];
    }
  },

  // Get single quotation by ID WITH ERROR HANDLING - FIXED ENDPOINT
  getQuotationById: async (id) => {
    try {
      const res = await axiosInstance.get(`/quotations/${id}`); // CHANGED: /quotation → /quotations
      return res.data;
    } catch (error) {
      console.error('❌ Get quotation error:', error.response?.data || error.message);
      return null;
    }
  },

  // Create new quotation WITH ERROR HANDLING - FIXED ENDPOINT
  createQuotation: async (data) => {
    try {
      console.log('📦 Sending quotation data:', data);
      const res = await axiosInstance.post('/quotations', data); // CHANGED: /quotation → /quotations
      return res.data;
    } catch (error) {
      console.error('❌ Create quotation error:', error.response?.data || error.message);
      throw error;
    }
  },

  // Update quotation WITH ERROR HANDLING - FIXED ENDPOINT
  updateQuotation: async (id, data) => {
    try {
      const res = await axiosInstance.put(`/quotations/${id}`, data); // CHANGED: /quotation → /quotations
      return res.data;
    } catch (error) {
      console.error('❌ Update quotation error:', error.response?.data || error.message);
      throw error;
    }
  },

  // There is deliberately no deleteQuotation. The backend mounts no DELETE on
  // /quotations, so the helper that used to sit here 404'd on every call. A
  // quotation is a document the customer has seen and the thing sales orders,
  // purchase orders and invoices all hang off — it is rejected or cancelled
  // through updateQuotationStatus, never removed.

  // Get next quotation number WITH ERROR HANDLING - FIXED ENDPOINT
  getNextQuotationNumber: async () => {
    try {
      const res = await axiosInstance.get('/quotations/next-number'); // CHANGED: /quotation → /quotations
      return res.data.number;
    } catch (error) {
      console.error('❌ Error getting next quotation number:', error);
      const year = new Date().getFullYear();
      return `QT-${year}-001`;
    }
  },

  // Update quotation status WITH ERROR HANDLING - FIXED ENDPOINT
  updateQuotationStatus: async (id, status) => {
    try {
      const res = await axiosInstance.patch(`/quotations/${id}/status`, { status }); // CHANGED: /quotation → /quotations
      return res.data;
    } catch (error) {
      console.error('❌ Update status error:', error.response?.data || error.message);
      throw error;
    }
  },

  // Send for approval (using status update)
  sendForApproval: async (id) => {
    return quotationAPI.updateQuotationStatus(id, 'PendingApproval');
  },

  // Approve quotation (using status update)
  approveQuotation: async (id) => {
    return quotationAPI.updateQuotationStatus(id, 'Approved');
  },

  // Get quotations by status WITH ERROR HANDLING - FIXED ENDPOINT
  getQuotationsByStatus: async (status) => {
    try {
      const res = await axiosInstance.get(`/quotations?status=${status}`); // CHANGED: /quotation → /quotations
      return res.data;
    } catch (error) {
      console.error('❌ Get by status error:', error.response?.data || error.message);
      return [];
    }
  },

  // Aggregated counts by status, role-scoped on the server.
  // Returns [{ status: 'Approved', count: 3 }, ...]
  getQuotationStats: async (range = {}) => {
    try {
      const res = await axiosInstance.get('/quotations/stats', { params: range });
      return res.data;
    } catch (error) {
      console.error('❌ Get quotation stats error:', error.response?.data || error.message);
      return [];
    }
  },

  // Total count + sum of total_amount (role-scoped on the server).
  // Returns { count: number, total_amount: number }
  getQuotationTotals: async (range = {}) => {
    try {
      const res = await axiosInstance.get('/quotations/totals', { params: range });
      return res.data;
    } catch (error) {
      console.error('❌ Get quotation totals error:', error.response?.data || error.message);
      return { count: 0, total_amount: 0 };
    }
  },

  // Check if lead is locked (enquiry locking system)
  isLeadLocked: async (leadId) => {
    try {
      const res = await axiosInstance.get(`/quotations/lead/${leadId}/lock-status`);
      return res.data.locked;
    } catch (error) {
      console.error('❌ Check lock status error:', error.response?.data || error.message);
      return false;
    }
  },

  // Get lock information (who locked, when, why)
  getLeadLockInfo: async (leadId) => {
    try {
      const res = await axiosInstance.get(`/quotations/lead/${leadId}/lock-info`);
      return res.data;
    } catch (error) {
      console.error('❌ Get lock info error:', error.response?.data || error.message);
      return null;
    }
  },

  // Get quotation by lead ID
  getQuotationByLeadId: async (leadId) => {
    try {
      const res = await axiosInstance.get(`/quotations/by-lead/${leadId}`);
      return res.data;
    } catch (error) {
      console.error('❌ Get quotation by lead error:', error.response?.data || error.message);
      return null;
    }
  },

  // Unlock enquiry (manager/admin only)
  unlockEnquiry: async (leadId) => {
    try {
      const res = await axiosInstance.patch(`/quotations/lead/${leadId}/unlock`);
      return res.data;
    } catch (error) {
      console.error('❌ Unlock enquiry error:', error.response?.data || error.message);
      throw error;
    }
  },

  // ========================================================
  // ENQ-LEVEL LOCK METHODS (check/unlock by enquiry_number directly)
  // ========================================================

  isEnquiryLocked: async (enquiryNumber) => {
    try {
      const res = await axiosInstance.get(`/quotations/enq/${encodeURIComponent(enquiryNumber)}/lock-status`);
      return res.data.locked;
    } catch (error) {
      console.error('❌ Check ENQ lock status error:', error.response?.data || error.message);
      return false;
    }
  },

  getEnquiryLockInfo: async (enquiryNumber) => {
    try {
      const res = await axiosInstance.get(`/quotations/enq/${encodeURIComponent(enquiryNumber)}/lock-info`);
      return res.data;
    } catch (error) {
      console.error('❌ Get ENQ lock info error:', error.response?.data || error.message);
      return null;
    }
  },

  unlockEnquiryByNumber: async (enquiryNumber) => {
    try {
      const res = await axiosInstance.patch(`/quotations/enq/${encodeURIComponent(enquiryNumber)}/unlock`);
      return res.data;
    } catch (error) {
      console.error('❌ Unlock ENQ error:', error.response?.data || error.message);
      throw error;
    }
  },

  // ========================================================
  // NEW: QUOTATION REVISION METHODS
  // ========================================================

  // Get revision history (all versions of a quotation)
  getRevisionHistory: async (quotationId) => {
    try {
      const res = await axiosInstance.get(`/quotations/${quotationId}/revisions`);
      return res.data;
    } catch (error) {
      console.error('❌ Get revision history error:', error.response?.data || error.message);
      return { parent_id: null, total_versions: 0, revisions: [] };
    }
  },

  // Create a new revision of a quotation
  createRevision: async (quotationId, revisionData) => {
    try {
      console.log('📝 Creating revision:', { quotationId, revisionData });
      const res = await axiosInstance.post(`/quotations/${quotationId}/create-revision`, revisionData);
      return res.data;
    } catch (error) {
      console.error('❌ Create revision error:', error.response?.data || error.message);
      throw error;
    }
  },

  // ========================================================
  // Deal outcome (Open/Won/Lost) — independent of the internal approval
  // `status` above. See migrations/026_quotation_deal_status.sql.
  // ========================================================

  setDealStatus: async (id, { deal_status, customer_reference_number, confirmation_file_url, lost_reason }) => {
    const res = await axiosInstance.patch(`/quotations/${id}/deal-status`, {
      deal_status, customer_reference_number, confirmation_file_url, lost_reason
    });
    return res.data;
  },

  // Pipeline cleanup list — committed (Approved/Sent) quotations still deal_status
  // 'Open' that nobody has touched in `days` (default 60, server-side).
  getStaleOpen: async (days) => {
    const res = await axiosInstance.get('/quotations/stale', { params: days ? { days } : {} });
    return res.data;
  },

  uploadConfirmationFile: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axiosInstance.post(`/quotations/${id}/confirmation-file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  },

  // Uploads a client-rendered PDF blob (see utils/exportPdf.js) so it has a
  // real hosted URL to share via WhatsApp/email.
  uploadPdf: async (id, blob, fileName) => {
    const formData = new FormData();
    formData.append('file', blob, fileName || 'quotation.pdf');
    const res = await axiosInstance.post(`/quotations/${id}/upload-pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  },

  // The WhatsApp share opens wa.me in a new tab and never touches the server,
  // so the quotation's Sent status has to be recorded explicitly.
  markSent: async (id) => {
    const response = await axiosInstance.post(`/quotations/${id}/mark-sent`);
    return response.data;
  },

  sendEmail: async (id, { to, subject, message, pdf_url }) => {
    const res = await axiosInstance.post(`/quotations/${id}/send-email`, { to, subject, message, pdf_url });
    return res.data;
  },

  // ========================================================
  // NEW: Get vendor prices for an enquiry (for quotation form)
  // ========================================================
  getVendorPricesByEnquiry: async (enquiryNumber) => {
    try {
      const res = await axiosInstance.get(`/quotations/vendors/by-enquiry/${enquiryNumber}`);
      return res.data;
    } catch (error) {
      console.error('❌ Get vendor prices error:', error.response?.data || error.message);
      return { products: [] };
    }
  }
};

export default quotationAPI;